import { Message } from '@arco-design/web-vue'
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { AxiosHeaders, HttpStatusCode } from 'axios'
import { assign, cloneDeep } from 'lodash-es'
import { i18n } from '@/plugins/i18n'
import { getToken } from '@/utils/auth'
import type { RbResponse } from '@/types/misc/http'

// 重写请求头url
export function handleRewriteConfig(config: InternalAxiosRequestConfig) {
  const url = config.url ?? ''
  const reg = /\b(api|rdxapi|bo-pri)\//
  if (reg.test(url)) {
    config.url = url.replace(reg, '')
  }
  return config
}

export function handleChangeRequestHeader(config: InternalAxiosRequestConfig) {
  if (!Array.isArray(config.transformResponse)) {
    config.transformResponse = []
  }
  config.transformResponse.push((data, _headers, status) => {
    if (status !== 200) {
      return data
    }
    return transformSuccessResponse(data, config)
  })
  return config
}

export function handleConfigureAuth(config: InternalAxiosRequestConfig) {
  const token = getToken()
  if (!config.headers) {
    config.headers = new AxiosHeaders()
  }
  config.headers.set('Authorization', `Basic c3RwOnN0cF9zZWNyZXQ=`)
  config.headers.set('platForm', `customer`)
  config.headers.set('Tenant-Id', '000000')
  config.headers.set('Blade-Auth', `bearer ${token}`)
  config.headers.set('Accept-Language', i18n.global.locale.value)
  config.headers.set('current-url', window.location.href)
  config.headers.set('Blade-Requested-With', 'With:BladeHttpRequest')
  // config.headers.set('Cookies', document.cookie)
  return config
}

export function handleResponseLog(response: AxiosResponse<RbResponse<unknown>>) {
  console.group('请求返回值')
  console.log(response.data)
  console.groupEnd()
}

export function handleRequestLog(config: InternalAxiosRequestConfig) {
  console.group('请求参数')
  console.log('url:', config.url)
  console.log('data:', config.data)
  console.log('params:', config.params)
  // console.log('token:', config.headers['Blade-Auth'])
  console.groupEnd()
  return config
}

export function handleNetworkError(response: AxiosResponse<RbResponse<unknown>>) {
  let errMessage = i18n.global.t('common.request.defaultError')
  if (response?.status) {
    switch (response.status) {
      // case HttpStatusCode.BadRequest: // 400
      //   errMessage = '错误的请求'
      //   break
      case HttpStatusCode.Unauthorized: // 401
        errMessage = i18n.global.t('common.request.unauthorized')
        break
      case HttpStatusCode.Forbidden: // 403
        errMessage = i18n.global.t('common.request.forbidden')
        break
      case HttpStatusCode.NotFound: // 404
        errMessage = i18n.global.t('common.request.notFound')
        break
      // case HttpStatusCode.MethodNotAllowed: // 405
      //   errMessage = '请求方法未允许'
      //   break
      // case HttpStatusCode.RequestTimeout: // 408
      //   errMessage = '请求超时'
      //   break
      // case HttpStatusCode.InternalServerError: // 500
      //   errMessage = '服务器端出错'
      //   break
      // case HttpStatusCode.NotImplemented: // 501
      //   errMessage = '网络未实现'
      //   break
      // case HttpStatusCode.BadGateway: // 502
      //   errMessage = '网络错误'
      //   break
      // case HttpStatusCode.ServiceUnavailable: // 503
      //   errMessage = '服务不可用'
      //   break
      // case HttpStatusCode.GatewayTimeout: // 504
      //   errMessage = '网络超时'
      //   break
      // case HttpStatusCode.HttpVersionNotSupported: // 505
      //   errMessage = 'http版本不支持该请求'
      //   break
      default:
        errMessage = response.data.msg
    }
  }

  Message.error(errMessage)
}

export function handleAuthError(errno: number) {
  const authErrMap: Record<number, string> = {
    10031: '登录失效，需要重新登录', // token 失效
    10032: '您太久没登录，请重新登录~', // token 过期
    10033: '账户未绑定角色，请联系管理员绑定角色',
    10034: '该用户未注册，请联系管理员注册用户',
    10035: 'code 无法获取对应第三方平台用户',
    10036: '该账户未关联员工，请联系管理员做关联',
    10037: '账号已无效',
    10038: '账号未找到',
  }

  if (Object.prototype.hasOwnProperty.call(authErrMap, errno)) {
    Message.error(authErrMap[errno])
    // 授权错误，登出账户
    // logout()
    return false
  }

  return true
}

export function handleGeneralError(code: number, msg: string) {
  if (code !== 200) {
    Message.error(msg)
    return false
  }

  return true
}

export function transformSuccessResponse<T = unknown>(response: AxiosResponse<RbResponse<T>>, config: InternalAxiosRequestConfig) {
  const url = config?.url ?? ''

  if (/\/blade-auth\/cas\/token\?grant_type=cas$/i.test(url)) {
    return cloneDeep({
      code: 200,
      success: true,
      data: cloneDeep(response),
      msg: '操作成功',
    }) as unknown as AxiosResponse<RbResponse<T>>
  }
  return response
}

export function transformErrorResponse<T = unknown>(response: AxiosResponse<RbResponse<T>>) {
  const responseData = response.data as RbResponse<T> & { reason?: string, data?: { reason?: string } }
  return assign(cloneDeep(response), {
    data: {
      data: undefined,
      content: response.data?.content,
      reason: responseData.reason ?? responseData.data?.reason,
      code: response.data?.code || response.data.return_code || response.status,
      success: false,
      msg: response.data?.msg || response.data?.return_msg || i18n.global.t('common.request.unknownError'),
    },
  })
}
